import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Slider,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AccountBalanceWalletOutlinedIcon from "@material-ui/icons/AccountBalanceWalletOutlined";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import CloseIcon from "@material-ui/icons/Close";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ReceiptOutlinedIcon from "@material-ui/icons/ReceiptOutlined";
import ShowChartOutlinedIcon from "@material-ui/icons/ShowChartOutlined";
import { compose } from "recompose";
import * as uuid from "uuid";
import * as ROUTES from "../../constants/route";
import { withAuthorization } from "../Session";
import NavigationMenus from "../NavigationMenus";
import Functions from "../MyInvestments/Functions";
import PerformanceGraph from "../MyInvestments/PerformanceGraph";
import { bankItems } from "../AccountUpdate/options";
import { rv } from "../AccountUpdate/tokens";
import {
  buildRedemptionRequest,
  getFullRedemptionConfirmation,
  getRedemptionSelection,
  getRedemptionSurveyError,
} from "./redemptionState";

const UNAVAILABLE_HOLDING_ERROR_CODES = [
  "investment-not-found",
  "investment-ownership-mismatch",
];

const REASONS = [
  "Realized my gains",
  "Switching funds",
  "Another product",
  "Emergency",
  "Dissatisfied",
  "Other",
];

const useStyles = makeStyles((theme) => ({
  page: {
    maxWidth: 1220,
    margin: "0 auto",
    paddingBottom: theme.spacing(12),
    fontFamily: rv.fontSans,
    color: rv.body,
    [theme.breakpoints.down("sm")]: {
      paddingBottom: 96,
    },
  },
  breadcrumbs: { marginBottom: theme.spacing(1) },
  backLink: {
    minHeight: 40,
    padding: theme.spacing(0.75, 1),
    color: rv.green,
    borderRadius: rv.radiusInput,
    fontWeight: 600,
    textTransform: "none",
  },
  title: {
    margin: `0 0 ${theme.spacing(2)}px`,
    color: rv.heading,
    fontSize: 28,
    fontWeight: 800,
    lineHeight: 1.2,
    [theme.breakpoints.down("xs")]: { fontSize: 24 },
  },
  stepTracker: {
    position: "sticky",
    top: theme.spacing(1),
    zIndex: 3,
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2.5),
    padding: theme.spacing(1.5, 2.5),
    border: `1px solid ${rv.hairline}`,
    borderRadius: rv.radiusCard,
    background: rv.card,
    boxShadow: rv.shadowCard,
    [theme.breakpoints.down("xs")]: {
      justifyContent: "center",
      padding: theme.spacing(1.25),
    },
  },
  step: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    color: rv.muted,
  },
  stepDot: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 30,
    height: 30,
    flex: "0 0 30px",
    border: `1px dashed ${rv.muted3}`,
    borderRadius: "50%",
    boxSizing: "border-box",
    background: rv.card,
    color: rv.muted,
    fontSize: 13,
    fontWeight: 700,
  },
  stepDotDone: {
    border: 0,
    background: rv.green,
    color: rv.onDark,
  },
  stepDotCurrent: {
    border: 0,
    background: rv.gold,
    color: rv.onDark,
    animation: "$pulse 2s ease-in-out infinite",
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  },
  stepLabel: {
    marginLeft: theme.spacing(1),
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: "nowrap",
    [theme.breakpoints.down("xs")]: { display: "none" },
  },
  stepCurrent: { color: rv.heading },
  stepConnector: {
    height: 2,
    flex: "1 1 50px",
    minWidth: 20,
    margin: theme.spacing(0, 1.25),
    background: rv.hairline,
  },
  stepConnectorDone: { background: rv.green },
  layout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(300px, 392px)",
    gap: theme.spacing(2.5),
    "@media (max-width: 1120px)": { gridTemplateColumns: "1fr" },
  },
  main: { minWidth: 0 },
  rail: {
    position: "sticky",
    top: 100,
    alignSelf: "start",
    maxWidth: 392,
    width: "100%",
    justifySelf: "end",
    "@media (max-width: 1120px)": {
      position: "static",
      maxWidth: "none",
    },
  },
  card: {
    marginBottom: theme.spacing(2),
    overflow: "hidden",
    border: `1px solid ${rv.hairline}`,
    borderRadius: rv.radiusCard,
    background: rv.card,
    boxShadow: rv.shadowCard,
  },
  cardBody: { padding: theme.spacing(2.5) },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.25),
    padding: theme.spacing(2.25, 2.5, 0),
  },
  iconTile: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    flex: "0 0 36px",
    borderRadius: 10,
    background: rv.mint2,
    color: rv.green,
  },
  cardTitle: {
    margin: 0,
    color: rv.heading,
    fontSize: 16,
    fontWeight: 700,
  },
  cardSubtitle: {
    margin: theme.spacing(0.5, 0, 0),
    color: rv.muted,
    fontSize: 12,
    lineHeight: 1.45,
  },
  fundName: {
    margin: 0,
    color: rv.heading,
    fontSize: 18,
    fontWeight: 800,
    lineHeight: 1.3,
  },
  fundMeta: {
    margin: theme.spacing(0.75, 0, 0),
    color: rv.muted,
    fontSize: 13,
  },
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    borderTop: `1px solid ${rv.hairline3}`,
    marginTop: theme.spacing(2),
    "@media (max-width: 900px)": {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    "@media (max-width: 430px)": { gridTemplateColumns: "1fr" },
  },
  stat: {
    minWidth: 0,
    padding: theme.spacing(1.75, 2),
    borderRight: `1px solid ${rv.hairline3}`,
    "&:last-child": { borderRight: 0 },
    "@media (max-width: 900px)": {
      borderBottom: `1px solid ${rv.hairline3}`,
      "&:nth-child(2n)": { borderRight: 0 },
    },
    "@media (max-width: 430px)": { borderRight: 0 },
  },
  statLabel: {
    display: "block",
    color: rv.muted,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  statValue: {
    display: "block",
    marginTop: 5,
    color: rv.heading,
    fontSize: 15,
    fontWeight: 700,
    overflowWrap: "anywhere",
  },
  statDetail: { display: "block", marginTop: 4, color: rv.muted, fontSize: 11 },
  unavailable: { color: rv.errorFg },
  performanceButton: {
    display: "flex",
    width: "100%",
    minHeight: 48,
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.25, 2.5),
    border: 0,
    borderTop: `1px solid ${rv.hairline3}`,
    background: "transparent",
    color: rv.green,
    cursor: "pointer",
    font: "inherit",
    fontSize: 13,
    fontWeight: 700,
  },
  graph: { minHeight: 280, padding: theme.spacing(0, 2.5, 2.5) },
  banner: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5, 1.75),
    border: "1px solid",
    borderRadius: 12,
    fontSize: 13,
    lineHeight: 1.5,
  },
  bannerGold: {
    color: rv.pendingFg,
    borderColor: rv.pendingBorder,
    background: rv.pendingBg,
  },
  bannerError: {
    color: "#9D3128",
    borderColor: "#F1B5B5",
    background: rv.errorBg,
  },
  bannerInfo: {
    color: rv.infoTitle,
    borderColor: rv.infoBorder,
    background: rv.infoBg,
  },
  amountLabel: {
    display: "block",
    marginBottom: theme.spacing(1),
    color: rv.heading,
    fontSize: 18,
    fontWeight: 700,
  },
  amountRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    [theme.breakpoints.down("xs")]: { flexDirection: "column" },
  },
  amountField: { flex: "1 1 auto" },
  allButton: {
    minWidth: 118,
    height: 56,
    borderRadius: rv.radiusInput,
    color: rv.green,
    borderColor: rv.green,
    fontWeight: 700,
    textTransform: "none",
  },
  allButtonActive: {
    color: rv.onDark,
    borderColor: rv.green,
    background: rv.green,
    "&:hover": { background: rv.green700 },
  },
  helper: {
    margin: theme.spacing(1, 0, 0),
    color: rv.muted,
    fontSize: 12,
    lineHeight: 1.5,
  },
  fieldError: {
    margin: theme.spacing(1, 0, 0),
    color: "#B5332B",
    fontSize: 12,
    fontWeight: 600,
  },
  slider: {
    margin: theme.spacing(1, 1, 0),
    color: rv.green,
    "& [class*='PrivateValueLabel-label']": { color: rv.onDark },
  },
  estimate: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: theme.spacing(0.75, 2),
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.75, 2),
    border: `1px solid ${rv.infoBorder}`,
    borderRadius: 12,
    background: rv.mint,
  },
  estimateLabel: { color: rv.body, fontSize: 13 },
  estimateValue: {
    color: rv.green900,
    fontSize: 17,
    fontWeight: 800,
    textAlign: "right",
  },
  estimateNote: {
    gridColumn: "1 / -1",
    color: rv.muted,
    fontSize: 11,
    lineHeight: 1.45,
  },
  summaryHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    background: rv.green,
    color: rv.onDark,
  },
  summaryTitle: { margin: 0, fontSize: 15, fontWeight: 700 },
  summaryBody: { margin: 0, padding: theme.spacing(0.5, 2) },
  summaryRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.15fr)",
    gap: theme.spacing(1.25),
    padding: theme.spacing(1.4, 0),
    borderBottom: `1px solid ${rv.hairline3}`,
  },
  summaryLabel: { color: rv.muted, fontSize: 12, lineHeight: 1.4 },
  summaryValue: {
    color: rv.heading,
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.4,
    textAlign: "right",
    overflowWrap: "anywhere",
  },
  summaryValueMuted: { color: rv.muted, fontStyle: "italic", fontWeight: 400 },
  summaryNet: { color: rv.green900, fontSize: 15, fontWeight: 800 },
  summaryNote: {
    display: "flex",
    gap: 7,
    padding: theme.spacing(1.5, 2),
    color: rv.muted,
    fontSize: 11,
    lineHeight: 1.45,
  },
  reviewList: { margin: 0, padding: 0, listStyle: "none" },
  reviewRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: theme.spacing(2),
    padding: theme.spacing(1.4, 0),
    borderBottom: `1px solid ${rv.hairline3}`,
    fontSize: 13,
  },
  reviewLabel: { color: rv.muted, textAlign: "left" },
  reviewValue: {
    color: rv.heading,
    fontWeight: 700,
    textAlign: "right",
    overflowWrap: "anywhere",
  },
  settlementActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  textButton: {
    padding: theme.spacing(0.5),
    color: rv.green,
    fontWeight: 700,
    textTransform: "none",
  },
  settlementWarning: {
    margin: theme.spacing(0, 0, 2),
    color: rv.pendingFg,
    fontSize: 12,
    lineHeight: 1.5,
  },
  accountType: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  accountTypeButton: {
    flex: 1,
    minHeight: 42,
    borderRadius: rv.radiusInput,
    color: rv.green,
    borderColor: rv.green,
    textTransform: "none",
    fontWeight: 700,
  },
  accountTypeButtonActive: {
    color: rv.onDark,
    borderColor: rv.green,
    background: rv.green,
    "&:hover": { background: rv.green700 },
  },
  reasonWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
  reason: {
    minHeight: 38,
    borderRadius: rv.radiusPill,
    color: rv.green,
    borderColor: rv.green,
    textTransform: "none",
    fontWeight: 600,
  },
  reasonActive: {
    color: rv.onDark,
    background: rv.green,
    borderColor: rv.green,
    "&:hover": { background: rv.green700 },
  },
  consentCard: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.25, 2),
    border: `1px solid ${rv.green300}`,
    borderRadius: rv.radiusCard,
    background: rv.mint,
  },
  consent: { width: "100%", margin: 0 },
  consentCheckbox: {
    color: rv.green,
    "&.Mui-checked": { color: rv.green },
  },
  consentLabel: {
    color: rv.body,
    fontSize: 14,
    lineHeight: 1.5,
  },
  termsButton: {
    minHeight: 0,
    padding: 0,
    color: rv.green,
    fontWeight: 700,
    textTransform: "none",
    textDecoration: "underline",
    verticalAlign: "baseline",
  },
  actionBar: {
    position: "sticky",
    bottom: 0,
    zIndex: 3,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(3),
    padding: theme.spacing(1.25, 0),
    borderTop: `1px solid ${rv.hairline2}`,
    background: rv.card,
    "@media (max-width: 900px)": {
      position: "fixed",
      right: 0,
      bottom: 0,
      left: 0,
      padding: theme.spacing(1.25, 2),
      paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 10px)",
      background: rv.card,
      boxShadow: "0 -6px 20px rgba(0,0,0,0.08)",
      zIndex: 1200,
    },
    [theme.breakpoints.down("xs")]: { gap: theme.spacing(1) },
  },
  actionHint: {
    flex: "1 1 auto",
    color: rv.muted,
    fontSize: 11,
    lineHeight: 1.35,
  },
  backButton: {
    minWidth: 92,
    minHeight: 44,
    borderRadius: rv.radiusInput,
    color: rv.green,
    borderColor: rv.green,
    textTransform: "none",
    fontWeight: 700,
  },
  primaryButton: {
    minWidth: 168,
    minHeight: 44,
    border: "2px solid transparent",
    borderRadius: rv.radiusInput,
    background: rv.green,
    color: rv.onDark,
    boxShadow: "none",
    textTransform: "none",
    fontWeight: 800,
    "&:hover": { background: rv.green700 },
    "&:disabled": {
      borderColor: "#D9D2C2",
      background: "#D9D2C2",
      color: "#FFFFFF",
    },
  },
  statusCard: { maxWidth: 680, margin: "0 auto", textAlign: "center" },
  statusIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 62,
    height: 62,
    borderRadius: "50%",
    background: rv.verifiedBg,
    color: rv.verifiedFg,
  },
  statusTitle: {
    margin: theme.spacing(1.5, 0, 0),
    color: rv.heading,
    fontSize: 24,
    fontWeight: 800,
  },
  statusBody: {
    maxWidth: 510,
    margin: theme.spacing(1, "auto", 2.5),
    color: rv.muted,
    fontSize: 14,
    lineHeight: 1.6,
  },
  statusActions: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down("xs")]: { flexDirection: "column" },
  },
  dialogPaper: {
    ["@media (max-width: 900px)"]: {
      alignSelf: "flex-end",
      width: "100%",
      margin: 0,
      borderRadius: "20px 20px 0 0",
    },
  },
  dialogClose: { position: "absolute", top: 8, right: 8, color: rv.muted },
  dialogContent: { color: rv.body, lineHeight: 1.6 },
  "@keyframes pulse": {
    "0%, 100%": { boxShadow: "0 0 0 0 rgba(200,147,42,0.28)" },
    "50%": { boxShadow: "0 0 0 5px rgba(200,147,42,0.12)" },
  },
}));

function formatCurrency(value) {
  if (!Number.isFinite(value)) return "To be computed";
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatShares(value) {
  if (!Number.isFinite(value)) return "-";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(value);
}

function hasSupportedShareDecimals(value) {
  return /^\d*\.?\d{0,4}$/.test(value);
}

function formatNavps(value) {
  if (!Number.isFinite(value)) return "Unavailable";
  return `₱${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(value)}`;
}

function formatDate(value) {
  if (!value) return "Latest available date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-PH", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function maskAccountNumber(value) {
  if (!value) return "Not provided";
  const visible = value.slice(-4);
  return `${"•".repeat(Math.max(0, value.length - visible.length))}${visible}`;
}

function isCompleteAccount(account) {
  return Boolean(
    account?.accountName?.trim() &&
      account?.bankName?.trim() &&
      account?.bankAccountNumber?.trim() &&
      typeof account?.savingsAccount === "boolean"
  );
}

function getGraphPoints(snapshot) {
  const points = [];
  snapshot.forEach((doc) => {
    (doc.data()?.months || []).forEach((month) => {
      ["week1", "week2", "week3", "week4"].forEach((week) => {
        (month[week] || []).forEach((entry) => {
          const timestamp = entry.date?.seconds
            ? entry.date.seconds * 1000
            : new Date(entry.date).getTime();
          if (
            Number.isFinite(timestamp) &&
            Number.isFinite(Number(entry.rate))
          ) {
            points.push([timestamp, Number(entry.rate)]);
          }
        });
      });
    });
  });
  return points;
}

function StepTracker({ step }) {
  const classes = useStyles();
  const steps = [
    "Number of Units",
    "Review Settlement & Survey",
    "Agreed to Terms and Submitted",
  ];
  return (
    <nav className={classes.stepTracker} aria-label="Redemption progress">
      {steps.map((label, index) => {
        const position = index + 1;
        const complete = position < step;
        const current = position === step;
        return (
          <React.Fragment key={label}>
            <div
              className={classes.step}
              aria-current={current ? "step" : undefined}
            >
              <span
                className={`${classes.stepDot} ${
                  complete
                    ? classes.stepDotDone
                    : current
                    ? classes.stepDotCurrent
                    : ""
                }`}
              >
                {complete ? (
                  <CheckCircleOutlineIcon fontSize="small" aria-hidden="true" />
                ) : (
                  position
                )}
              </span>
              <span
                className={`${classes.stepLabel} ${
                  current ? classes.stepCurrent : ""
                }`}
              >
                {position}. {label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <span
                className={`${classes.stepConnector} ${
                  complete ? classes.stepConnectorDone : ""
                }`}
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

function SummaryRail({ quote, selectedShares }) {
  const classes = useStyles();
  const rate = quote.navps.value;
  const gross =
    Number.isFinite(rate) && Number.isFinite(selectedShares)
      ? rate * selectedShares
      : null;
  const fee = Number.isFinite(gross) ? quote.estimate.serviceFee : null;
  const net = Number.isFinite(gross) ? gross - fee : null;
  const rows = [
    ["Units", formatShares(selectedShares)],
    ["Latest NAVPU", formatNavps(rate)],
    ["Estimated gross", formatCurrency(gross)],
    ["Service fee", formatCurrency(fee)],
    ["Est. net proceeds", formatCurrency(net), true],
  ];
  return (
    <aside
      className={`${classes.card} ${classes.rail}`}
      aria-label="Redemption Summary"
      aria-live="polite"
    >
      <div className={classes.summaryHeader}>
        <ReceiptOutlinedIcon fontSize="small" aria-hidden="true" />
        <h2 className={classes.summaryTitle}>Redemption Summary</h2>
      </div>
      <dl className={classes.summaryBody}>
        {rows.map(([label, value, emphasized]) => (
          <div className={classes.summaryRow} key={label}>
            <dt className={classes.summaryLabel}>{label}</dt>
            <dd
              className={`${classes.summaryValue} ${
                !Number.isFinite(rate) ? classes.summaryValueMuted : ""
              } ${emphasized ? classes.summaryNet : ""}`}
            >
              {value}
            </dd>
          </div>
        ))}
      </dl>
      <div className={classes.summaryNote}>
        <InfoOutlinedIcon fontSize="small" aria-hidden="true" />
        <span>
          {quote.fund.redemptionSettlementTimeline
            ? `Fund-provided settlement timeline: ${quote.fund.redemptionSettlementTimeline}.`
            : "Settlement timing is confirmed by the fund company."}{" "}
          Estimates are not guaranteed; final proceeds use the NAVPU on the
          confirmed dealing date.
        </span>
      </div>
    </aside>
  );
}

function LoadingOrError({ loading, error, onRetry, onBack }) {
  const classes = useStyles();
  if (loading) {
    return (
      <div className={classes.card} role="status" aria-busy="true">
        <div className={classes.cardBody}>Loading redemption details...</div>
      </div>
    );
  }
  return (
    <div className={classes.card} role="alert">
      <div className={classes.cardBody}>
        <div className={`${classes.banner} ${classes.bannerError}`}>
          <ErrorOutlineIcon aria-hidden="true" />
          <span>{error || "Redemption details could not be loaded."}</span>
        </div>
        <Button className={classes.primaryButton} onClick={onRetry}>
          Try again
        </Button>
        <Button className={classes.backLink} onClick={onBack}>
          Back to investments
        </Button>
      </div>
    </div>
  );
}

function RedemptionFlow({ firebase }) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { investmentUid } = useParams();
  const functionsRef = useRef(new Functions());
  const requestIdRef = useRef(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [step, setStep] = useState(1);
  const [sharesInput, setSharesInput] = useState("");
  const [redeemAll, setRedeemAll] = useState(false);
  const [conversionRequired, setConversionRequired] = useState(false);
  const [conversionAccepted, setConversionAccepted] = useState(false);
  const [conversionOpen, setConversionOpen] = useState(false);
  const [performanceOpen, setPerformanceOpen] = useState(false);
  const [performanceData, setPerformanceData] = useState([]);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsText, setTermsText] = useState("");
  const [termsError, setTermsError] = useState("");
  const [editingAccount, setEditingAccount] = useState(false);
  const [accountChanged, setAccountChanged] = useState(false);
  const [accountDraft, setAccountDraft] = useState({
    accountName: "",
    bankName: "",
    bankAccountNumber: "",
    savingsAccount: null,
  });
  const [reasons, setReasons] = useState([]);
  const [otherReason, setOtherReason] = useState("");
  const [feedback, setFeedback] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError("");
    functionsRef.current
      .getRedemptionQuote(investmentUid)
      .then((response) => {
        if (!active) return;
        const nextQuote = response.data?.data;
        if (!nextQuote)
          throw new Error("Redemption details could not be loaded.");
        setQuote(nextQuote);
        setAccountDraft(nextQuote.settlementAccount || {});
        if (nextQuote.eligibility.state === "fullOnly") {
          setRedeemAll(true);
          setSharesInput(String(nextQuote.holding.availableShares));
        } else if (Number.isFinite(nextQuote.limits.minimumShares)) {
          setSharesInput(String(nextQuote.limits.minimumShares));
        }
      })
      .catch((error) => {
        if (!active) return;
        if (
          UNAVAILABLE_HOLDING_ERROR_CODES.includes(error.response?.data?.code)
        ) {
          history.replace("/404");
          return;
        }
        setLoadError(error.response?.data?.message || error.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [history, investmentUid, reloadKey]);

  useEffect(() => {
    let active = true;
    if (!firebase?.db) return undefined;
    firebase.db
      .collection("miscellaneous")
      .doc("redemptionTerms")
      .get()
      .then((snapshot) => {
        if (!active) return;
        setTermsText(snapshot.data()?.content || "");
        setTermsError(
          snapshot.exists ? "" : "Redemption Terms are unavailable."
        );
      })
      .catch(() => {
        if (active) setTermsError("Redemption Terms are unavailable.");
      });
    return () => {
      active = false;
    };
  }, [firebase]);

  useEffect(() => {
    let active = true;
    if (!performanceOpen || !quote?.fund?.id || !firebase?.db) return undefined;
    setPerformanceLoading(true);
    firebase.db
      .collection("funds")
      .doc(quote.fund.id)
      .collection("summary")
      .orderBy("dateCompiled", "asc")
      .get()
      .then((snapshot) => {
        if (active) setPerformanceData(getGraphPoints(snapshot));
      })
      .catch(() => {
        if (active) setPerformanceData([]);
      })
      .finally(() => {
        if (active) setPerformanceLoading(false);
      });
    return () => {
      active = false;
    };
  }, [firebase, performanceOpen, quote]);

  const goBack = () => {
    const context = location.state || {};
    history.push({
      pathname: ROUTES.INVESTMENT_HISTORY,
      state: context.fundCode
        ? { fundCode: context.fundCode, companyId: context.companyId }
        : undefined,
    });
  };

  if (loading || loadError || !quote) {
    return (
      <LoadingOrError
        loading={loading}
        error={loadError}
        onRetry={() => setReloadKey((value) => value + 1)}
        onBack={goBack}
      />
    );
  }

  const rate = quote.navps.value;
  const navpsUnavailable = quote.eligibility.state === "navpsUnavailable";
  const blockedInProgress = quote.eligibility.state === "blockedInProgress";
  const fullOnly =
    quote.eligibility.state === "fullOnly" || quote.limits.fullRedemptionOnly;
  const availableShares = quote.holding.availableShares;
  const typedShares = Number(sharesInput);
  const selectedShares = redeemAll || fullOnly ? availableShares : typedShares;
  const gross =
    Number.isFinite(rate) && Number.isFinite(selectedShares)
      ? rate * selectedShares
      : null;
  const estimatedNet = Number.isFinite(gross)
    ? gross - quote.estimate.serviceFee
    : null;
  const accountComplete = isCompleteAccount(accountDraft);
  const isOtherSelected = reasons.includes("Other");
  const surveyError = getRedemptionSurveyError({ reasons, otherReason });
  const otherReasonError =
    isOtherSelected && surveyError ? surveyError : "";

  let amountError = "";
  if (navpsUnavailable)
    amountError =
      "Redemption is unavailable until the latest NAVPU is published.";
  else if (blockedInProgress) amountError = quote.eligibility.message;
  else if (!redeemAll && !fullOnly) {
    if (!sharesInput.trim())
      amountError = "Enter the number of units to redeem.";
    else if (!Number.isFinite(typedShares) || typedShares <= 0)
      amountError = "Enter a valid number of units.";
    else if (!hasSupportedShareDecimals(sharesInput.trim()))
      amountError = "Enter units with up to 4 decimal places.";
    else if (typedShares < quote.limits.minimumShares)
      amountError = `Enter at least ${formatShares(
        quote.limits.minimumShares
      )} units.`;
    else if (typedShares > quote.limits.maximumShares)
      amountError = `You can redeem up to ${formatShares(
        quote.limits.maximumShares
      )} units and maintain the minimum balance.`;
  }
  if (!amountError && conversionRequired && !conversionAccepted) {
    amountError =
      "Review and confirm the required full redemption to continue.";
  }
  if (!amountError && Number.isFinite(gross) && gross > quote.limits.maxGross) {
    amountError = "This request exceeds the PHP 500,000 maximum.";
  }

  const toggleReason = (reason) => {
    setReasons((current) =>
      current.includes(reason)
        ? current.filter((item) => item !== reason)
        : [...current, reason]
    );
  };

  const updateAccount = (field, value) => {
    setAccountDraft((current) => ({ ...current, [field]: value }));
  };

  const selectRedeemAll = () => {
    if (redeemAll && !fullOnly) {
      setRedeemAll(false);
      setConversionRequired(false);
      setConversionAccepted(false);
      setSharesInput(String(quote.limits.minimumShares));
      return;
    }
    setRedeemAll(true);
    setConversionRequired(false);
    setConversionAccepted(false);
    setSharesInput(String(availableShares));
  };

  const updateAmount = (value) => {
    setConversionRequired(false);
    setConversionAccepted(false);
    const valueAsNumber = Number(value);
    if (Number.isFinite(valueAsNumber) && valueAsNumber === availableShares) {
      setRedeemAll(true);
      setSharesInput(String(availableShares));
      return;
    }

    setRedeemAll(false);
    setSharesInput(value);
    if (
      hasSupportedShareDecimals(value) &&
      valueAsNumber > 0 &&
      valueAsNumber < availableShares &&
      Number.isFinite(rate)
    ) {
      const selection = getRedemptionSelection(
        valueAsNumber,
        availableShares,
        rate
      );
      if (selection.requiresFullRedemptionConversion) {
        setRedeemAll(true);
        setConversionRequired(true);
        setSharesInput(String(availableShares));
        setConversionOpen(true);
      }
    }
  };

  const continueToReview = () => {
    if (conversionRequired && !conversionAccepted) {
      setConversionOpen(true);
      return;
    }
    if (!amountError) setStep(2);
  };

  const cancelFullRedemptionConversion = () => {
    setConversionOpen(false);
    setConversionRequired(false);
    setConversionAccepted(false);
    setRedeemAll(false);
    setSharesInput(String(quote.limits.maximumShares));
    setStep(1);
  };

  const saveAccountDraft = () => {
    if (!accountComplete) return;
    setEditingAccount(false);
    setAccountChanged(true);
  };

  const cancelAccountEdit = () => {
    setAccountDraft(quote.settlementAccount || {});
    setEditingAccount(false);
  };

  const submit = () => {
    if (
      submitting ||
      !accountComplete ||
      editingAccount ||
      !consent ||
      surveyError ||
      amountError
    )
      return;
    setSubmitting(true);
    setSubmitError("");
    if (!requestIdRef.current) requestIdRef.current = uuid.v4();
    const request = buildRedemptionRequest({
      fundCode: quote.holding.fundCode,
      shares: selectedShares,
      uuid: requestIdRef.current,
      investmentUid,
      surveyData: {
        question1: { answers: reasons, otherText: otherReason.trim() },
        question2: feedback.trim(),
      },
      ...(accountChanged ? accountDraft : {}),
      acceptFullRedemptionConversion: conversionRequired && conversionAccepted,
    });
    functionsRef.current
      .doRedeemInvestment(request)
      .then((response) => {
        const data = response.data?.data || {};
        setConfirmation({
          eventId: data.eventId,
          shares: data.effectiveShares || selectedShares,
          redemptionType:
            data.redemptionType || (redeemAll ? "full" : "partial"),
        });
        setStep(3);
      })
      .catch((error) => {
        const fullConfirmation = getFullRedemptionConfirmation(error, rate);
        if (fullConfirmation) {
          setQuote((current) => ({
            ...current,
            holding: {
              ...current.holding,
              availableShares: fullConfirmation.effectiveShares,
            },
            navps: { ...current.navps, value: fullConfirmation.rate },
          }));
          setSharesInput(String(fullConfirmation.effectiveShares));
          setRedeemAll(true);
          setConversionRequired(true);
          setConversionAccepted(false);
          setConversionOpen(true);
          return;
        }
        setSubmitError(
          error.response?.data?.message ||
            "We could not confirm the request status. Try again safely with the same details."
        );
      })
      .finally(() => setSubmitting(false));
  };

  const renderFundContext = () => (
    <section className={classes.card} aria-labelledby="redemption-fund-title">
      <div className={classes.cardHeader}>
        <span className={classes.iconTile} aria-hidden="true">
          <AccountBalanceWalletOutlinedIcon fontSize="small" />
        </span>
        <div>
          <h2 id="redemption-fund-title" className={classes.fundName}>
            {quote.fund.name}
          </h2>
          <p className={classes.fundMeta}>
            {quote.fund.companyName} ·{" "}
            {quote.fund.fundType || "Investment fund"}
          </p>
        </div>
      </div>
      <div className={classes.stats}>
        <div className={classes.stat}>
          <span className={classes.statLabel}>Available units</span>
          <strong className={classes.statValue}>
            {formatShares(availableShares)}
          </strong>
        </div>
        <div className={classes.stat}>
          <span className={classes.statLabel}>Market value</span>
          <strong className={classes.statValue}>
            {formatCurrency(
              Number.isFinite(rate) ? availableShares * rate : null
            )}
          </strong>
        </div>
        <div className={classes.stat}>
          <span className={classes.statLabel}>Latest NAVPU</span>
          <strong
            className={`${classes.statValue} ${
              navpsUnavailable ? classes.unavailable : ""
            }`}
          >
            {formatNavps(rate)}
          </strong>
          <span className={classes.statDetail}>
            {navpsUnavailable
              ? "Awaiting next publication"
              : `As of ${formatDate(quote.navps.asOf)}`}
          </span>
        </div>
        <div className={classes.stat}>
          <span className={classes.statLabel}>Settlement</span>
          <strong className={classes.statValue}>
            {quote.fund.redemptionSettlementTimeline || "Fund-provided"}
          </strong>
        </div>
      </div>
      <button
        className={classes.performanceButton}
        type="button"
        onClick={() => setPerformanceOpen((open) => !open)}
        aria-expanded={performanceOpen}
      >
        <span>
          <ShowChartOutlinedIcon
            fontSize="small"
            style={{ verticalAlign: "middle", marginRight: 8 }}
            aria-hidden="true"
          />
          Fund performance
        </span>
        {performanceOpen ? (
          <ExpandLessIcon aria-hidden="true" />
        ) : (
          <ExpandMoreIcon aria-hidden="true" />
        )}
      </button>
      <Collapse in={performanceOpen}>
        <div className={classes.graph}>
          <PerformanceGraph
            title="Historical NAVPU"
            data={performanceData}
            doneLoading={!performanceLoading}
            type="stockChart"
          />
        </div>
      </Collapse>
    </section>
  );

  const renderAmountStep = () => (
    <>
      {renderFundContext()}
      <section className={classes.card} aria-labelledby="redeem-amount-title">
        <div className={classes.cardHeader}>
          <span className={classes.iconTile} aria-hidden="true">
            <ReceiptOutlinedIcon fontSize="small" />
          </span>
          <div>
            <h2 id="redeem-amount-title" className={classes.cardTitle}>
              How much would you like to redeem?
            </h2>
            <p className={classes.cardSubtitle}>
              Enter units. Peso amounts are estimates until the fund confirms
              the dealing date.
            </p>
          </div>
        </div>
        <div className={classes.cardBody}>
          {fullOnly && (
            <div className={`${classes.banner} ${classes.bannerGold}`}>
              <InfoOutlinedIcon aria-hidden="true" />
              <span>
                Only a full redemption is available because this holding is
                below the minimum partial redemption amount. Your next
                investment in this fund will follow its minimum initial
                investment.
              </span>
            </div>
          )}
          {navpsUnavailable && (
            <div
              className={`${classes.banner} ${classes.bannerError}`}
              role="alert"
            >
              <ErrorOutlineIcon aria-hidden="true" />
              <span>
                Latest NAVPU is unavailable. You can return later when the fund
                publishes a new rate.{" "}
                <Button
                  className={classes.termsButton}
                  onClick={() => setReloadKey((value) => value + 1)}
                >
                  Refresh
                </Button>
              </span>
            </div>
          )}
          {!fullOnly && (
            <div className={`${classes.banner} ${classes.bannerInfo}`}>
              <InfoOutlinedIcon aria-hidden="true" />
              <span>
                Partial redemptions must leave at least PHP 1,000 invested in
                this fund. Requests that fall below this balance are processed
                as full redemptions.
              </span>
            </div>
          )}
          <label htmlFor="redemption-shares" className={classes.amountLabel}>
            Units to redeem
          </label>
          <div className={classes.amountRow}>
            <TextField
              id="redemption-shares"
              className={classes.amountField}
              variant="outlined"
              value={sharesInput}
              disabled={
                navpsUnavailable ||
                blockedInProgress ||
                fullOnly ||
                conversionRequired
              }
              error={Boolean(
                amountError && !navpsUnavailable && !blockedInProgress
              )}
              inputProps={{
                inputMode: "decimal",
                "aria-describedby": "redemption-shares-help",
              }}
              onChange={(event) =>
                updateAmount(event.target.value.replace(/,/g, ""))
              }
            />
            <Button
              variant={redeemAll ? "contained" : "outlined"}
              className={`${classes.allButton} ${
                redeemAll ? classes.allButtonActive : ""
              }`}
              disabled={navpsUnavailable || blockedInProgress || fullOnly}
              onClick={selectRedeemAll}
              aria-pressed={redeemAll}
            >
              {redeemAll ? "ALL UNITS" : "Redeem all"}
            </Button>
          </div>
          <p id="redemption-shares-help" className={classes.helper}>
            Available: {formatShares(availableShares)} units ·{" "}
            {formatCurrency(
              Number.isFinite(rate) ? availableShares * rate : null
            )}
          </p>
          {!fullOnly &&
            Number.isFinite(rate) &&
            quote.limits.maximumShares >= quote.limits.minimumShares && (
              <Slider
                className={classes.slider}
                value={
                  Number.isFinite(typedShares)
                    ? Math.min(
                        Math.max(typedShares, quote.limits.minimumShares),
                        availableShares
                      )
                    : quote.limits.minimumShares
                }
                min={quote.limits.minimumShares}
                max={availableShares}
                step={0.0001}
                disabled={
                  navpsUnavailable || blockedInProgress || conversionRequired
                }
                onChange={(_event, value) => updateAmount(String(value))}
                aria-label="Units to redeem"
                valueLabelDisplay="auto"
              />
            )}
          {amountError && !navpsUnavailable && !blockedInProgress && (
            <p className={classes.fieldError} role="alert">
              {amountError}
            </p>
          )}
          {conversionRequired && (
            <div
              className={`${classes.banner} ${classes.bannerGold}`}
              role="alert"
            >
              <InfoOutlinedIcon aria-hidden="true" />
              <span>
                Your remaining investment in this fund will fall below the
                required PHP 1,000 maintaining balance. Your request will
                therefore be processed as a full redemption.
              </span>
            </div>
          )}
          <div className={classes.estimate} aria-live="polite">
            <span className={classes.estimateLabel}>Estimated value</span>
            <strong className={classes.estimateValue}>
              {formatCurrency(gross)}
            </strong>
            <span className={classes.estimateNote}>
              Estimated service fee:{" "}
              {formatCurrency(
                Number.isFinite(gross) ? quote.estimate.serviceFee : null
              )}{" "}
              · Estimated net proceeds: {formatCurrency(estimatedNet)}. Final
              proceeds are determined at the confirmed dealing date.
            </span>
          </div>
        </div>
      </section>
    </>
  );

  const renderSettlement = () => (
    <section
      className={classes.card}
      aria-labelledby="settlement-account-title"
    >
      <div className={classes.cardHeader}>
        <span className={classes.iconTile} aria-hidden="true">
          <AccountBalanceWalletOutlinedIcon fontSize="small" />
        </span>
        <div>
          <h2 id="settlement-account-title" className={classes.cardTitle}>
            Settlement account
          </h2>
          <p className={classes.cardSubtitle}>
            Your redemption proceeds will be sent to this account.
          </p>
        </div>
      </div>
      <div className={classes.cardBody}>
        {!accountComplete && !editingAccount && (
          <div className={`${classes.banner} ${classes.bannerGold}`}>
            <InfoOutlinedIcon aria-hidden="true" />
            <span>
              No settlement account on file. Add bank details before submitting.
            </span>
          </div>
        )}
        {!editingAccount ? (
          <>
            <div className={classes.settlementActions}>
              <strong>
                {accountComplete ? "Registered account" : "No account saved"}
              </strong>
              <Button
                className={classes.textButton}
                onClick={() => setEditingAccount(true)}
              >
                {accountComplete ? "Edit bank details" : "Add bank details"}
              </Button>
            </div>
            {accountComplete && (
              <ul className={classes.reviewList}>
                <li className={classes.reviewRow}>
                  <span className={classes.reviewLabel}>Account name</span>
                  <strong className={classes.reviewValue}>
                    {accountDraft.accountName}
                  </strong>
                </li>
                <li className={classes.reviewRow}>
                  <span className={classes.reviewLabel}>Bank</span>
                  <strong className={classes.reviewValue}>
                    {accountDraft.bankName}
                  </strong>
                </li>
                <li className={classes.reviewRow}>
                  <span className={classes.reviewLabel}>Account number</span>
                  <strong className={classes.reviewValue}>
                    {maskAccountNumber(accountDraft.bankAccountNumber)}
                  </strong>
                </li>
                <li className={classes.reviewRow}>
                  <span className={classes.reviewLabel}>Account type</span>
                  <strong className={classes.reviewValue}>
                    {accountDraft.savingsAccount ? "Savings" : "Current"}
                  </strong>
                </li>
              </ul>
            )}
          </>
        ) : (
          <>
            <p className={classes.settlementWarning}>
              The account must be in your name. Third-party accounts are not
              accepted. Saving these details will overwrite your stored
              settlement account when you submit this redemption.
            </p>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Account name"
                  variant="outlined"
                  fullWidth
                  value={accountDraft.accountName || ""}
                  onChange={(event) =>
                    updateAccount("accountName", event.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="redemption-bank-label">Bank</InputLabel>
                  <Select
                    labelId="redemption-bank-label"
                    label="Bank"
                    value={accountDraft.bankName || ""}
                    onChange={(event) =>
                      updateAccount("bankName", event.target.value)
                    }
                  >
                    {bankItems.map((bank) => (
                      <MenuItem key={bank.value} value={bank.value}>
                        {bank.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Account number"
                  variant="outlined"
                  fullWidth
                  value={accountDraft.bankAccountNumber || ""}
                  onChange={(event) =>
                    updateAccount("bankAccountNumber", event.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <span className={classes.helper}>Account type</span>
                <div className={classes.accountType}>
                  <Button
                    variant="outlined"
                    className={`${classes.accountTypeButton} ${
                      accountDraft.savingsAccount === false
                        ? classes.accountTypeButtonActive
                        : ""
                    }`}
                    onClick={() => updateAccount("savingsAccount", false)}
                    aria-pressed={accountDraft.savingsAccount === false}
                  >
                    Current
                  </Button>
                  <Button
                    variant="outlined"
                    className={`${classes.accountTypeButton} ${
                      accountDraft.savingsAccount === true
                        ? classes.accountTypeButtonActive
                        : ""
                    }`}
                    onClick={() => updateAccount("savingsAccount", true)}
                    aria-pressed={accountDraft.savingsAccount === true}
                  >
                    Savings
                  </Button>
                </div>
              </Grid>
            </Grid>
            {!accountComplete && (
              <p className={classes.fieldError} role="alert">
                Complete all settlement account fields.
              </p>
            )}
            <DialogActions style={{ padding: "20px 0 0" }}>
              <Button className={classes.backLink} onClick={cancelAccountEdit}>
                Cancel
              </Button>
              <Button
                className={classes.primaryButton}
                disabled={!accountComplete}
                onClick={saveAccountDraft}
              >
                Save bank details
              </Button>
            </DialogActions>
          </>
        )}
      </div>
    </section>
  );

  const renderReviewStep = () => (
    <>
      <section className={classes.card} aria-labelledby="review-title">
        <div className={classes.cardHeader}>
          <span className={classes.iconTile} aria-hidden="true">
            <ReceiptOutlinedIcon fontSize="small" />
          </span>
          <div>
            <h2 id="review-title" className={classes.cardTitle}>
              Transaction review
            </h2>
            <p className={classes.cardSubtitle}>
              Review estimated redemption details before submitting.
            </p>
          </div>
        </div>
        <div className={classes.cardBody}>
          <ul className={classes.reviewList}>
            <li className={classes.reviewRow}>
              <span className={classes.reviewLabel}>Fund</span>
              <strong className={classes.reviewValue}>{quote.fund.name}</strong>
            </li>
            <li className={classes.reviewRow}>
              <span className={classes.reviewLabel}>Units</span>
              <strong className={classes.reviewValue}>
                {formatShares(selectedShares)}
              </strong>
            </li>
            <li className={classes.reviewRow}>
              <span className={classes.reviewLabel}>Latest NAVPU</span>
              <strong className={classes.reviewValue}>
                {formatNavps(rate)} · {formatDate(quote.navps.asOf)}
              </strong>
            </li>
            <li className={classes.reviewRow}>
              <span className={classes.reviewLabel}>Estimated gross</span>
              <strong className={classes.reviewValue}>
                {formatCurrency(gross)}
              </strong>
            </li>
            <li className={classes.reviewRow}>
              <span className={classes.reviewLabel}>Service fee</span>
              <strong className={classes.reviewValue}>
                {formatCurrency(quote.estimate.serviceFee)}
              </strong>
            </li>
            <li className={classes.reviewRow}>
              <span className={classes.reviewLabel}>
                Estimated net proceeds
              </span>
              <strong className={classes.reviewValue}>
                {formatCurrency(estimatedNet)}
              </strong>
            </li>
            <li className={classes.reviewRow}>
              <span className={classes.reviewLabel}>Expected settlement</span>
              <strong className={classes.reviewValue}>
                {quote.fund.redemptionSettlementTimeline ||
                  "Fund-provided timeline"}
              </strong>
            </li>
          </ul>
          <p className={classes.helper}>
            <InfoOutlinedIcon
              fontSize="small"
              aria-hidden="true"
              style={{ verticalAlign: "middle", marginRight: 6 }}
            />
            All peso amounts are estimates. Final proceeds use the NAVPU on the
            confirmed dealing date.
          </p>
        </div>
      </section>
      {renderSettlement()}
      <section className={classes.card} aria-labelledby="survey-title">
        <div className={classes.cardHeader}>
          <span className={classes.iconTile} aria-hidden="true">
            <InfoOutlinedIcon fontSize="small" />
          </span>
          <div>
            <h2 id="survey-title" className={classes.cardTitle}>
              Why are you redeeming?
            </h2>
            <p className={classes.cardSubtitle}>
              Select at least one reason. Feedback is optional.
            </p>
          </div>
        </div>
        <div className={classes.cardBody}>
          <div className={classes.reasonWrap}>
            {REASONS.map((reason) => (
              <Button
                key={reason}
                variant="outlined"
                className={`${classes.reason} ${
                  reasons.includes(reason) ? classes.reasonActive : ""
                }`}
                onClick={() => toggleReason(reason)}
                aria-pressed={reasons.includes(reason)}
              >
                {reason}
              </Button>
            ))}
          </div>
          {isOtherSelected && (
            <TextField
              label="Please specify"
              variant="outlined"
              fullWidth
              margin="normal"
              value={otherReason}
              onChange={(event) => setOtherReason(event.target.value)}
              error={Boolean(otherReasonError)}
              helperText={otherReasonError}
            />
          )}
          <TextField
            label="Feedback or suggestions"
            variant="outlined"
            fullWidth
            multiline
            rows={3}
            margin="normal"
            value={feedback}
            onChange={(event) => setFeedback(event.target.value)}
          />
        </div>
      </section>
      <section className={classes.consentCard} aria-labelledby="consent-title">
        <FormControlLabel
          className={classes.consent}
          control={
            <Checkbox
              className={classes.consentCheckbox}
              checked={consent}
              onChange={(event) => {
                setConsent(event.target.checked);
                if (event.target.checked) setTermsOpen(true);
              }}
              color="default"
              inputProps={{ "aria-describedby": "consent-title" }}
            />
          }
          label={
            <span id="consent-title" className={classes.consentLabel}>
              I agree to the{" "}
              <Button
                className={classes.termsButton}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setTermsOpen(true);
                }}
              >
                Redemption Terms
              </Button>
              .
            </span>
          }
        />
        {termsError && (
          <p className={classes.fieldError} role="alert">
            {termsError}
          </p>
        )}
      </section>
      {submitError && (
        <div
          className={`${classes.banner} ${classes.bannerError}`}
          role="alert"
        >
          <ErrorOutlineIcon aria-hidden="true" />
          <span>{submitError}</span>
        </div>
      )}
    </>
  );

  const renderConfirmation = () => (
    <section
      className={`${classes.card} ${classes.statusCard}`}
      aria-labelledby="redemption-confirmation-title"
    >
      <div className={classes.cardBody}>
        <span className={classes.statusIcon} aria-hidden="true">
          <CheckCircleOutlineIcon fontSize="large" />
        </span>
        <h2 id="redemption-confirmation-title" className={classes.statusTitle}>
          Your redemption request was submitted
        </h2>
        <p className={classes.statusBody}>
          We will review your redemption request and process according to the
          fund’s processing timeline below. We will email you the complete
          settlement details once confirmed and redeemed.
        </p>
        <ul className={classes.reviewList}>
          <li className={classes.reviewRow}>
            <span className={classes.reviewLabel}>Reference ID</span>
            <strong
              className={classes.reviewValue}
              style={{ fontFamily: "Roboto Mono, monospace" }}
            >
              {confirmation.eventId}
            </strong>
          </li>
          <li className={classes.reviewRow}>
            <span className={classes.reviewLabel}>Units requested</span>
            <strong className={classes.reviewValue}>
              {formatShares(confirmation.shares)}
            </strong>
          </li>
          <li className={classes.reviewRow}>
            <span className={classes.reviewLabel}>Redemption processing timeline:</span>
            <strong className={classes.reviewValue}>
              {quote.fund.redemptionSettlementTimeline ||
                "Fund-provided timeline"}
            </strong>
          </li>
        </ul>
        <div className={classes.statusActions}>
          <Button
            className={classes.primaryButton}
            onClick={() =>
              history.push(
                `${ROUTES.INVESTMENT_HISTORY}/${confirmation.eventId}`
              )
            }
          >
            View redemption tracker
          </Button>
          <Button
            className={classes.backButton}
            variant="outlined"
            onClick={goBack}
          >
            Back to my investments
          </Button>
        </div>
      </div>
    </section>
  );

  if (blockedInProgress) {
    return (
      <div className={classes.page}>
        <Breadcrumbs className={classes.breadcrumbs} aria-label="breadcrumb">
          <Link
            component="button"
            type="button"
            color="textSecondary"
            onClick={goBack}
          >
            Investment History
          </Link>
          <Typography color="textPrimary" aria-current="page">
            Redemption
          </Typography>
        </Breadcrumbs>
        <h1 className={classes.title}>Redeem Investment</h1>
        <section className={`${classes.card} ${classes.statusCard}`}>
          <div className={classes.cardBody}>
            <span className={classes.statusIcon}>
              <InfoOutlinedIcon fontSize="large" />
            </span>
            <h2 className={classes.statusTitle}>
              A transaction is already in progress for this fund
            </h2>
            <p className={classes.statusBody}>
              You can follow the existing request from your investment history.
            </p>
            <Button
              className={classes.primaryButton}
              onClick={() =>
                quote.inProgressTransaction?.eventId
                  ? history.push(
                      `${ROUTES.INVESTMENT_HISTORY}/${quote.inProgressTransaction.eventId}`
                    )
                  : goBack()
              }
            >
              View in-progress redemption
            </Button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className={classes.page}>
      <Breadcrumbs className={classes.breadcrumbs} aria-label="breadcrumb">
        <Link
          component="button"
          type="button"
          color="textSecondary"
          onClick={goBack}
        >
          Investment History
        </Link>
        <Typography color="textPrimary" aria-current="page">
          Redemption
        </Typography>
      </Breadcrumbs>
      <h1 className={classes.title}>Redeem Investment</h1>
      <StepTracker step={step} />
      {step === 3 ? (
        renderConfirmation()
      ) : (
        <div className={classes.layout}>
          <main className={classes.main}>
            {step === 1 ? renderAmountStep() : renderReviewStep()}
          </main>
          <SummaryRail quote={quote} selectedShares={selectedShares} />
        </div>
      )}
      {step < 3 && (
        <div className={classes.actionBar}>
          {step > 1 ? (
            <Button
              variant="outlined"
              className={classes.backButton}
              disabled={submitting}
              onClick={() => setStep(1)}
              startIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
          ) : (
            <span />
          )}
          <span className={classes.actionHint}>
            {step === 1
              ? amountError || "Choose the units you would like to redeem."
              : editingAccount
              ? "Save or cancel bank details before submitting."
              : !accountComplete
              ? "Add a complete settlement account to continue."
              : surveyError ||
                (!consent ? "Agree to the Redemption Terms to submit." : "")}
          </span>
          {step === 1 ? (
            <Button
              className={classes.primaryButton}
              disabled={Boolean(amountError)}
              onClick={continueToReview}
            >
              Continue
            </Button>
          ) : (
            <Button
              className={classes.primaryButton}
              disabled={
                submitting ||
                !accountComplete ||
                editingAccount ||
                !consent ||
                Boolean(surveyError) ||
                Boolean(amountError)
              }
              onClick={submit}
              startIcon={
                submitting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : undefined
              }
            >
              {submitting ? "Submitting..." : "Submit redemption"}
            </Button>
          )}
        </div>
      )}
      <Dialog
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
        fullWidth
        maxWidth="md"
        aria-labelledby="redemption-terms-title"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle id="redemption-terms-title">
          Redemption Terms
          <Button
            className={classes.dialogClose}
            aria-label="Close terms"
            onClick={() => setTermsOpen(false)}
          >
            <CloseIcon />
          </Button>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {termsError ? (
            <div role="alert">{termsError}</div>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: termsText }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            className={classes.primaryButton}
            onClick={() => setTermsOpen(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={conversionOpen}
        onClose={cancelFullRedemptionConversion}
        aria-labelledby="full-redemption-title"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle id="full-redemption-title">
          Full redemption required
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          This partial redemption would leave less than PHP 1,000 invested.
          Confirm to redeem all {formatShares(availableShares)} available units
          instead.
        </DialogContent>
        <DialogActions>
          <Button
            className={classes.backLink}
            onClick={cancelFullRedemptionConversion}
          >
            Cancel
          </Button>
          <Button
            className={classes.primaryButton}
            onClick={() => {
              setConversionAccepted(true);
              setConversionOpen(false);
            }}
          >
            Redeem all units
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

function RedemptionPage(props) {
  return (
    <NavigationMenus
      disableFab
      content={<RedemptionFlow firebase={props.firebase} />}
    />
  );
}

const condition = (authUser) => !!authUser;

export default compose(withAuthorization(condition))(RedemptionPage);
